import { useAuth, useCurrentUser } from "@roastery/auth";
import { useTranslation } from "@roastery/i18n";
import { Button, ThemeSwitcher } from "@roastery/ui";
import { LogOutIcon, MenuIcon } from "lucide-react";
import { LanguageSwitcher } from "./language-switcher";

/**
 * Who is signed in, and out. There is deliberately no roastery name here: an operator belongs to no
 * roastery, and the panel's whole point is that it looks at all of them at once.
 */
export function AdminHeader({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const { user, isLoading } = useCurrentUser();

  return (
    <header
      className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-4 md:gap-4 md:px-6 print:hidden"
      data-testid="admin-header"
    >
      <Button
        variant="ghost"
        size="sm"
        className="md:hidden"
        aria-label={t("nav.openMenu")}
        data-testid="nav-open"
        onClick={onOpenMenu}
      >
        <MenuIcon />
      </Button>

      <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="admin-scope">
        {t("platform.shell.scope")}
      </span>

      <div className="flex items-center gap-2 md:gap-4">
        <ThemeSwitcher
          groupLabel={t("theme.label")}
          labels={{ light: t("theme.light"), dark: t("theme.dark"), system: t("theme.system") }}
        />
        <LanguageSwitcher />

        <span className="hidden text-sm font-medium sm:inline" data-testid="admin-user">
          {isLoading ? t("app.loading") : (user?.email ?? "—")}
        </span>

        <Button
          variant="outline"
          size="sm"
          data-testid="sign-out"
          onClick={() => void auth.signoutRedirect()}
        >
          <LogOutIcon />
          <span className="hidden sm:inline">{t("header.signOut")}</span>
        </Button>
      </div>
    </header>
  );
}
